import { Theme } from "@mui/material";
import dayjs from "dayjs";
import { BarSeriesOption } from "echarts";
import { orderBy, startCase, sumBy } from "lodash";
import { useMemo } from "react";

import { FormatCurrencyFunction } from "@/hooks/useConfig/types";
import { formatDateLocale } from "@/shared/utils/formatDate";
import Palette from "@/Utils/Colors/palette";

import { useBusinessTermMapping } from "../../financial-review/utils/businessTermMapping";
import { SalesCompositionV2DTO } from "../api/getSalesCompositionV2";

export type ExtendedBarSeriesOption = BarSeriesOption & {
  children?: Array<{
    name: string;
    data: number[];
    displayName?: string;
  }>;
  section?: string;
  displayName?: string;
};

type UseSalesCompositionChartOptionsProps = {
  salesCompositionData: SalesCompositionV2DTO["chartView"] | undefined;
  isDollarViewSelected: boolean;
  formatCurrency: FormatCurrencyFunction;
  palette: Palette;
  activeFilterIds: string[];
  theme: Theme;
};

const OTHER_COLOR = "#8D6FCC";

type ColorMap = {
  sectionColors: Map<string, string>;
  itemColors: Map<string, string>;
  getItemColor: (section: string, itemName: string, index: number) => string;
  getSectionColor: (section: string) => string;
};

export const useSalesCompositionChartOptions = ({
  salesCompositionData,
  isDollarViewSelected,
  palette,
  activeFilterIds,
  theme,
}: UseSalesCompositionChartOptionsProps): {
  seriesData: ExtendedBarSeriesOption[];
  axisLabels: string[];
  totalValues: number[];
  activeFilterIds: string[];
  filterLegendItems: Array<{
    id: string;
    title: string;
    items: Array<{
      id: string;
      title: string;
      isActive: boolean;
      color: string;
    }>;
  }>;
  hiddenFilterCount: number;
  flatLegendItems: Array<{
    id: string;
    title: string;
    isActive: boolean;
    color: string;
    sectionId: string;
    sectionTitle: string;
    value: number;
  }>;
  legendRestCount: number;
  legendHiddenCount: number;
  isGroupedLegend: boolean;
  groupedLegendItems: Array<{
    id: string;
    title: string;
    color: string;
    isActive: boolean;
  }>;
  individualLegendItems: Array<{
    id: string;
    title: string;
    color: string;
    isActive: boolean;
    section: string;
  }>;
} => {
  const { mapBusinessTermName } = useBusinessTermMapping();

  // Initialize color management system
  const colorMap = useMemo((): ColorMap => {
    const data = isDollarViewSelected
      ? salesCompositionData?.y_axis
      : salesCompositionData?.y_axis_unit;

    if (!data) {
      return {
        sectionColors: new Map(),
        itemColors: new Map(),
        getItemColor: () => OTHER_COLOR,
        getSectionColor: () => OTHER_COLOR,
      };
    }

    const sectionColors = new Map<string, string>();
    const itemColors = new Map<string, string>();

    // Assign colors to sections
    Object.keys(data).forEach((section, index) => {
      sectionColors.set(section, palette.getSequentialColor(index + 12).color);
    });

    // Count items in other_factors for color offset
    const otherFactorsCount = Object.keys(data["other_factors"] || {}).length;

    // Assign colors to items
    Object.entries(data).forEach(([section, sectionData]) => {
      Object.entries(sectionData).forEach(([itemName], index) => {
        const colorIndex = section === "other_factors" ? index : index + otherFactorsCount;
        itemColors.set(itemName, palette.getSequentialColor(colorIndex).color);
      });
    });

    return {
      sectionColors,
      itemColors,
      getItemColor: (section: string, itemName: string, index: number) => {
        if (itemName === "base_sale") return theme.palette.baseSales.color;
        if (section === "other_factors") {
          return itemColors.get(itemName) || palette.getSequentialColor(index).color;
        }
        return (
          itemColors.get(itemName) || palette.getSequentialColor(index + otherFactorsCount).color
        );
      },
      getSectionColor: (section: string) => sectionColors.get(section) || OTHER_COLOR,
    };
  }, [salesCompositionData, isDollarViewSelected, palette, theme]);

  // Process the data once to get both the chart series and legend items
  const processedData = useMemo(() => {
    const data = isDollarViewSelected
      ? salesCompositionData?.y_axis
      : salesCompositionData?.y_axis_unit;

    if (!data) return { series: [], legendItems: [], allItems: [] };

    // 1. Flatten all items from all sections
    const allItems: Array<{
      section: string;
      name: string;
      data: number[];
      total: number;
    }> = [];
    Object.entries(data).forEach(([sectionName, sectionData]) => {
      Object.entries(sectionData).forEach(([itemName, values]) => {
        allItems.push({
          section: sectionName,
          name: itemName,
          data: values as number[],
          total: (values as number[]).reduce((sum, val) => sum + val, 0),
        });
      });
    });

    // 2. Build chart series grouped by section
    const series: ExtendedBarSeriesOption[] = [];

    // Check if all active items are from the same section
    const activeItems = allItems.filter((item) => activeFilterIds.includes(item.name));
    const activeSections = new Set(activeItems.map((item) => item.section));
    const isSingleSection = activeSections.size === 1;

    // Group items by section
    Object.entries(data).forEach(([sectionName]) => {
      const sectionItems = allItems.filter((item) => item.section === sectionName);
      if (
        sectionName === "other_factors" ||
        (isSingleSection && sectionName === Array.from(activeSections)[0])
      ) {
        // For other_factors or when all active items are from this section, create individual series
        sectionItems.forEach((item, index) => {
          if (activeFilterIds.includes(item.name)) {
            series.push({
              name: item.name,
              data: item.data,
              color: colorMap.getItemColor(sectionName, item.name, index),
              section: sectionName,
              displayName: mapBusinessTermName(item.name),
            });
          }
        });
      } else {
        // For other sections, keep the grouped approach
        const filteredSectionItems = sectionItems.filter((item) =>
          activeFilterIds.includes(item.name)
        );
        if (filteredSectionItems.length > 0) {
          const aggregatedData =
            filteredSectionItems[0]?.data.map((_, idx) =>
              filteredSectionItems.reduce((sum, item) => {
                const arr = Array.isArray(item.data) ? item.data : [];
                return sum + (arr[idx] ?? 0);
              }, 0)
            ) || [];
          series.push({
            name: sectionName,
            data: aggregatedData,
            color: colorMap.getSectionColor(sectionName),
            section: sectionName,
            displayName: startCase(sectionName),
            children: filteredSectionItems.map((item) => ({
              name: item.name,
              data: item.data,
              displayName: mapBusinessTermName(item.name),
            })),
          });
        }
      }
    });

    // 3. Build grouped legend items for the filter
    const groupedLegendItemsForFilter = Object.entries(data)
      .map(([section, sectionData]) => {
        const isActiveSection = isSingleSection && section === Array.from(activeSections)[0];
        const items = Object.entries(sectionData).map(([itemName], index) => ({
          id: itemName,
          title: mapBusinessTermName(itemName),
          isActive: activeFilterIds.includes(itemName),
          color:
            section === "other_factors" || isActiveSection
              ? colorMap.getItemColor(section, itemName, index)
              : colorMap.getSectionColor(section),
        }));
        return {
          id: section,
          title: startCase(section),
          items,
        };
      })
      .filter((group) => group.items.length > 0);

    return { series, legendItems: groupedLegendItemsForFilter, allItems };
  }, [salesCompositionData, isDollarViewSelected, activeFilterIds, mapBusinessTermName, colorMap]);

  const seriesData = useMemo(() => {
    return processedData.series
      .filter((series) => {
        if (series.children) {
          return series.children.some((child) => activeFilterIds.includes(child.name));
        }
        return activeFilterIds.includes(String(series.name));
      })
      .map((series) => {
        if (series.children) {
          series.children = series.children.filter((child) => activeFilterIds.includes(child.name));
          series.data = series.children
            .map((child) => child.data)
            .reduce((acc, curr) => {
              return acc?.map((val, index) => val + curr[index]);
            }, Array(series.children[0]?.data?.length).fill(0));
        }
        return {
          ...series,
          name: series.displayName || series.name,
        };
      });
  }, [processedData.series, activeFilterIds]);

  const axisLabels =
    salesCompositionData?.x_axis.date.map((date) =>
      formatDateLocale(dayjs(date), { yearFormat: "YY" })
    ) || [];

  const totalValues = useMemo(() => {
    if (seriesData.length > 0) {
      const calculateTotalValues = (series: ExtendedBarSeriesOption[]): number[] => {
        const maxLength = Math.max(...series.map((s) => s.data?.length || 0));
        const totals = new Array(maxLength).fill(0);

        series.forEach((s) => {
          if (s.data) {
            s.data.forEach((value, index) => {
              totals[index] += value;
            });
          }
        });
        return totals;
      };
      return calculateTotalValues(seriesData);
    }
    return [];
  }, [seriesData]);

  const totalFilters = processedData.legendItems.reduce(
    (sum, section) => sum + section.items.length,
    0
  );
  const hiddenFilterCount = totalFilters - activeFilterIds.length;

  // FLAT LEGEND LOGIC
  // Flatten all items from all sections for legend row
  const allLegendItems = processedData.legendItems.flatMap((section) =>
    section.items.map((item) => ({
      ...item,
      sectionId: section.id,
      sectionTitle: section.title,
    }))
  );
  // Attach value for sorting (find in allItems by id)
  const legendItemsWithValue = allLegendItems.map((item) => {
    const found = processedData.allItems?.find((i) => i.name === item.id);
    return {
      ...item,
      value: found ? sumBy(found.data, (v) => (typeof v === "number" ? v : 0)) : 0,
    };
  });
  const sortedLegendItems = orderBy(legendItemsWithValue, ["value"], ["desc"]);
  // Only show legend items that are active
  const activeSortedLegendItems = sortedLegendItems.filter((item) =>
    activeFilterIds.includes(item.id)
  );
  const flatLegendItems = activeSortedLegendItems;
  const legendRestCount = 0;
  const legendHiddenCount = allLegendItems.filter(
    (item) => !activeFilterIds.includes(item.id)
  ).length;

  // --- Legend logic for display ---
  // Check if all active items are from the same section
  const activeItems = processedData.allItems.filter((item) => activeFilterIds.includes(item.name));
  const activeSections = new Set(activeItems.map((item) => item.section));
  const isGroupedLegend = activeSections.size !== 1;

  // Grouped legend: one per section, but always break out 'Other Factors' as individual items
  let groupedLegendItems: Array<{ id: string; title: string; color: string; isActive: boolean }>;
  const data = isDollarViewSelected
    ? salesCompositionData?.y_axis
    : salesCompositionData?.y_axis_unit;

  if (data) {
    groupedLegendItems = Object.entries(data)
      .filter(([section]) => section !== "other_factors")
      .map(([section]) => {
        const sectionColor = colorMap.getSectionColor(section);
        return {
          id: section,
          title: startCase(section),
          color: sectionColor,
          isActive: activeItems.some((item) => item.section === section),
        };
      })
      .filter((item) => item.isActive);
    // Add each 'Other Factors' item as its own legend
    const otherFactors = Object.entries(data["other_factors"] || {})
      .map(([itemName], idx) => {
        const found = activeItems.find((i) => i.name === itemName && i.section === "other_factors");
        if (!found) return null;
        const color = colorMap.getItemColor("other_factors", itemName, idx);
        return {
          id: itemName,
          title: mapBusinessTermName(itemName),
          color,
          isActive: true,
        };
      })
      .filter(Boolean) as Array<{ id: string; title: string; color: string; isActive: boolean }>;
    groupedLegendItems = [...groupedLegendItems, ...otherFactors];
  } else {
    groupedLegendItems = [];
  }

  // Individual legend: one per item (already breaks out 'Other Factors')
  let individualLegendItems: Array<any> = [];
  if (data) {
    // Always break out 'other_factors' as individual items
    const otherFactors = Object.entries(data["other_factors"] || {})
      .map(([itemName], idx) => {
        const found = activeItems.find((i) => i.name === itemName && i.section === "other_factors");
        if (!found) return null;
        const color = colorMap.getItemColor("other_factors", itemName, idx);
        return {
          id: itemName,
          title: mapBusinessTermName(itemName),
          color,
          isActive: true,
          section: "other_factors",
          value: found.total,
        };
      })
      .filter(Boolean);
    // All other active items (not in 'other_factors')
    const nonOtherFactors = activeItems
      .filter((item) => item.section !== "other_factors")
      .map((item) => {
        let color = colorMap.getSectionColor(item.section);
        if (isGroupedLegend) {
          // grouped mode, but we only want this for color logic, not for legend
        } else {
          // split mode, use split color logic
          const sectionItems = processedData.allItems.filter((i) => i.section === item.section);
          const idx = sectionItems.findIndex((i) => i.name === item.name);
          const colorIndex = idx + otherFactors.length;
          color = colorMap.getItemColor(item.section, item.name, colorIndex);
        }
        return {
          id: item.name,
          title: mapBusinessTermName(item.name),
          color,
          isActive: true,
          section: item.section,
          value: item.total,
        };
      });
    individualLegendItems = [...otherFactors, ...nonOtherFactors];
  }

  // Helper to sort legend items by seriesData order: negatives first, then positives, both in original order
  const sortLegendBySeriesData = <T extends { title: string }>(
    legendItems: T[],
    seriesData: ExtendedBarSeriesOption[]
  ) => {
    const negatives = seriesData
      .filter((item) => Array.isArray(item.data) && (item.data?.[0] as number) < 0)
      .map((item) => item.name);
    const positives = seriesData
      .filter((item) => !Array.isArray(item.data) || (item.data?.[0] as number) >= 0)
      .map((item) => item.name);
    const sortedOrder = [...negatives, ...positives];
    return [...legendItems].sort(
      (a, b) => sortedOrder.indexOf(a.title) - sortedOrder.indexOf(b.title)
    );
  };

  return {
    seriesData,
    axisLabels,
    totalValues,
    activeFilterIds,
    filterLegendItems: processedData.legendItems,
    hiddenFilterCount,
    flatLegendItems,
    legendRestCount,
    legendHiddenCount,
    isGroupedLegend,
    groupedLegendItems: sortLegendBySeriesData(groupedLegendItems, seriesData),
    individualLegendItems: sortLegendBySeriesData(individualLegendItems, seriesData),
  };
};
